/**
 * Mission 2's ruined village: broken masonry with jagged tops. A wall is a row of stubs of
 * uneven height - the saw-tooth skyline is what says "ruin" at 18 px/m; cracks and missing
 * bricks would be sub-pixel. Brick in the temperate palette, the biome's concrete elsewhere.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { capColor } from './snow';

const TAU = Math.PI * 2;

const masonry = (p: Palette): string => (p.biome === 'temperate' ? 'brick' : 'concrete');

/** Freshly broken tops take the light step (dusted white in the arctic); end faces the dark one. */
function stubColors(p: Palette): BoxColors {
  const m = masonry(p);
  return { all: p.color(`${m}.mid`), top: capColor(p, `${m}.hi`), left: p.color(`${m}.lo`), right: p.color(`${m}.lo`) };
}

/**
 * Shell of a house: tallest at the back, stepping down along the sides to low remnants at
 * the front, so the camera looks into it. The sim treats the footprint as solid (2.2 m).
 */
export const ruinHouse: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const w = 5.2;
  const d = 4.4;
  const t = 0.42;
  const colors = stubColors(p);
  const stub = (x: number, z: number, sx: number, sz: number, h: number): void => {
    mb.box(sx, h, sz, colors, place(x, 0, z));
  };

  const north = [2.2, 2.8, 1.5, 2.6, 1.8];
  const nw = w / north.length;
  north.forEach((h, i) => stub(-w / 2 + nw * (i + 0.5), -d / 2 + t / 2, nw, t, h + rng.range(-0.25, 0.25)));
  // A window that survived, dark against the back wall.
  mb.box(0.7, 0.7, 0.06, p.color('ink.1'), place(-w / 2 + nw * 3.5, 1.1, -d / 2 + t + 0.01));

  const side = (d - t) / 4;
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 4; i++) {
      const tall = 2.5 - i * 0.45;
      stub(sx * (w / 2 - t / 2), -d / 2 + t + side * (i + 0.5), t, side, rng.range(tall * 0.62, tall));
    }
  }
  // What is left of the front, either side of the doorway.
  stub(-1.54, d / 2 - t / 2, 1.28, t, rng.range(0.8, 1.4));
  stub(1.69, d / 2 - t / 2, 0.98, t, rng.range(0.6, 1.2));

  // Rubble on the floor and one charred roof beam lying across it.
  const m = masonry(p);
  for (let i = 0; i < 5; i++) {
    const tone = p.color(rng.chance(0.5) ? `${m}.lo` : `${m}.mid`);
    const at = place(rng.range(-1.7, 1.7), 0, rng.range(-1.1, 1.3), rng.range(0, TAU), 1, rng.range(-0.2, 0.2));
    mb.box(rng.range(0.5, 0.95), rng.range(0.2, 0.45), rng.range(0.4, 0.8), tone, at);
  }
  mb.box(0.18, 0.18, 3.0, p.color('ink.1'), place(0.2, 0.3, 0, rng.range(0.5, 0.9), 1, 0.12));
  return mb;
};

/**
 * Broken wall along x. The sim blocks sight along the whole run, so most stubs stand above a
 * man's eye line; only the odd notch drops lower, and a continuous base course closes those.
 */
export function ruinWallRun(p: Palette, rng: Pcg32, length = 6): MeshBuilder {
  const mb = new MeshBuilder();
  const m = masonry(p);
  const colors = stubColors(p);
  mb.box(length, 0.5, 0.62, { all: p.color(`${m}.lo`), top: p.color(`${m}.mid`) });
  const n = Math.max(1, Math.round(length / 1.25));
  const seg = length / n;
  for (let i = 0; i < n; i++) {
    const h = rng.chance(0.2) ? rng.range(0.95, 1.2) : rng.range(1.4, 2.0);
    mb.box(seg, h, 0.56, colors, place(-length / 2 + seg * (i + 0.5), 0, 0));
  }
  return mb;
}
